import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, KeyRound, Lock, ShieldCheck, Mail } from "lucide-react";
import { forgotPassword, resetPassword } from "@/lib/api";
import Logo from "@/components/Logo";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const urlToken = params.get("token") || "";
  const urlEmail = params.get("email") || "";

  const [step, setStep] = useState(urlToken ? "reset" : "request"); // "request" | "reset" | "success"
  const [email, setEmail] = useState(urlEmail);
  const [otp, setOtp] = useState("");
  const [token, setToken] = useState(urlToken);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null);

  useEffect(() => {
    if (urlToken) {
      setToken(urlToken);
      setStep("reset");
    }
    if (urlEmail) {
      setEmail(urlEmail);
    }
  }, [urlToken, urlEmail]);

  // Password Policy checks
  const hasMinLength = newPassword.length >= 8;
  const hasUpper = /[A-Z]/.test(newPassword);
  const hasLower = /[a-z]/.test(newPassword);
  const hasDigit = /[0-9]/.test(newPassword);
  const hasSpecial = /[^A-Za-z0-9]/.test(newPassword);
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const isPasswordValid = hasMinLength && hasUpper && hasLower && hasDigit && hasSpecial && passwordsMatch;

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!email) {
      setError("Please enter your email address.");
      return;
    }
    setError(null);
    setInfoMessage(null);
    setLoading(true);
    try {
      const res = await forgotPassword(email);
      setInfoMessage(res?.message || "Password reset OTP and link sent to your email.");
      setStep("reset");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to request password reset.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError(null);
    if (!otp && !token) {
      setError("Please enter the 6-digit OTP code received in your email.");
      return;
    }
    if (!isPasswordValid) {
      setError("Please make sure your password satisfies all policy requirements and passwords match.");
      return;
    }

    setLoading(true);
    try {
      await resetPassword({
        email,
        otp: otp ? otp.trim() : undefined,
        token: token ? token.trim() : undefined,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      setStep("success");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to reset password. Please verify your OTP/token.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-primary text-primary-foreground p-12 relative grain-overlay">
        <Link to="/login" className="inline-flex items-center gap-2 text-primary-foreground/80 hover:text-primary-foreground text-sm">
          <ArrowLeft size={14} /> Back to Sign In
        </Link>
        <div className="space-y-6">
          <Logo height={56} />
          <h2 className="serif text-4xl leading-tight max-w-md">
            Secure & Seamless <em className="italic" style={{ color: "hsl(45 85% 55%)" }}>Account Recovery</em>.
          </h2>
          <p className="text-primary-foreground/70 max-w-md text-sm leading-relaxed">
            Verify your identity with your 6-digit OTP or single-use security reset link to restore access to your TrustDime workspace.
          </p>
        </div>
        <div className="text-xs text-primary-foreground/50">
          © {new Date().getFullYear()} TrustDime · Powered by ITProFound
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="mb-6 flex items-center justify-between">
            <Link to="/login" className="lg:hidden inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
              <ArrowLeft size={14} /> Back to Sign In
            </Link>
          </div>

          {step === "request" && (
            <>
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-primary/10 text-primary">
                  <KeyRound size={28} />
                </div>
                <div>
                  <h1 className="serif text-2xl text-primary font-semibold">Forgot Password?</h1>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Enter your account email to receive a 6-digit OTP code & reset link.
                  </p>
                </div>
              </div>

              {error && (
                <div className="mt-6 rounded-md border border-destructive/30 bg-destructive/5 text-destructive px-4 py-3 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleRequestOtp} className="mt-6 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1">Account Email</label>
                  <div className="relative">
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@organization.com"
                      className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm pl-9 focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                    <Mail className="absolute left-3 top-3 text-muted-foreground" size={16} />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full rounded-md bg-primary text-primary-foreground px-4 py-3 text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60"
                >
                  {loading ? "Sending OTP..." : "Send Reset Code & Link"}
                </button>

                <div className="text-center pt-2">
                  <button
                    type="button"
                    onClick={() => setStep("reset")}
                    className="text-xs text-primary hover:underline"
                  >
                    Already have an OTP code or link? Click here to enter code
                  </button>
                </div>
              </form>
            </>
          )}

          {step === "reset" && (
            <>
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-primary/10 text-primary">
                  <Lock size={28} />
                </div>
                <div>
                  <h1 className="serif text-2xl text-primary font-semibold">Reset Your Password</h1>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Enter your OTP code and set your new account password.
                  </p>
                </div>
              </div>

              {infoMessage && (
                <div className="mt-6 rounded-md border border-primary/20 bg-primary/5 text-primary px-4 py-3 text-sm">
                  {infoMessage}
                </div>
              )}

              {error && (
                <div className="mt-6 rounded-md border border-destructive/30 bg-destructive/5 text-destructive px-4 py-3 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleResetPassword} className="mt-6 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1">Email Address</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@organization.com"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  />
                </div>

                {!urlToken && (
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1">6-Digit OTP Code</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      placeholder="123456"
                      className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-center text-lg font-mono tracking-widest"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-foreground mb-1">New Password</label>
                  <input
                    type="password"
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground mb-1">Confirm New Password</label>
                  <input
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  />
                </div>

                {/* Password policy requirements checklist */}
                <div className="rounded-md border border-border bg-accent/30 p-3 space-y-1.5 text-xs text-muted-foreground">
                  <div className="font-medium text-foreground mb-1">Password Requirements:</div>
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 size={13} className={hasMinLength ? "text-emerald-600" : "text-muted-foreground/40"} />
                    <span className={hasMinLength ? "text-foreground font-medium" : ""}>At least 8 characters</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 size={13} className={hasUpper ? "text-emerald-600" : "text-muted-foreground/40"} />
                    <span className={hasUpper ? "text-foreground font-medium" : ""}>At least one uppercase letter (A-Z)</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 size={13} className={hasLower ? "text-emerald-600" : "text-muted-foreground/40"} />
                    <span className={hasLower ? "text-foreground font-medium" : ""}>At least one lowercase letter (a-z)</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 size={13} className={hasDigit ? "text-emerald-600" : "text-muted-foreground/40"} />
                    <span className={hasDigit ? "text-foreground font-medium" : ""}>At least one number (0-9)</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 size={13} className={hasSpecial ? "text-emerald-600" : "text-muted-foreground/40"} />
                    <span className={hasSpecial ? "text-foreground font-medium" : ""}>At least one special character (!@#$%^&*)</span>
                  </div>
                  <div className="flex items-center gap-1.5 pt-1">
                    <CheckCircle2 size={13} className={passwordsMatch ? "text-emerald-600" : "text-muted-foreground/40"} />
                    <span className={passwordsMatch ? "text-foreground font-medium" : ""}>Passwords match</span>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || !isPasswordValid || (!otp && !token)}
                  className="w-full rounded-md bg-primary text-primary-foreground px-4 py-3 text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60"
                >
                  {loading ? "Resetting Password..." : "Reset Password & Update"}
                </button>

                <div className="flex items-center justify-between pt-2">
                  <button
                    type="button"
                    onClick={() => setStep("request")}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    ← Resend OTP code
                  </button>
                  <Link to="/login" className="text-xs text-muted-foreground hover:text-foreground">
                    Back to Sign In
                  </Link>
                </div>
              </form>
            </>
          )}

          {step === "success" && (
            <div className="text-center space-y-6">
              <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 flex items-center justify-center">
                <ShieldCheck size={32} />
              </div>
              <div className="space-y-2">
                <h2 className="serif text-2xl text-primary font-semibold">Password Reset Complete!</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Your password has been successfully updated. You can now sign in to your TrustDime account with your new password.
                </p>
              </div>

              <button
                type="button"
                onClick={() => navigate("/login")}
                className="w-full rounded-md bg-primary text-primary-foreground px-4 py-3 text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                Go to Sign In
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
